import { useMemo, useState } from "react";
import { catalog } from "@/mocks/catalog";
import { updates } from "@/mocks/updates";

interface HeroProps {
  onSearch: (query: string) => void;
}

const quickChips = ["蓝光4K", "悬疑", "纪录片", "已完结", "下饭剧"];

export default function Hero({ onSearch }: HeroProps) {
  const [value, setValue] = useState("");

  const stats = useMemo(() => {
    const total = catalog.length;
    const blu = catalog.filter((it) => it.quality.includes("4K")).length;
    const types = new Set(catalog.map((it) => it.type)).size;
    return { total, blu, types };
  }, []);

  const handleSubmit = (query: string) => {
    setValue(query);
    onSearch(query);
  };

  return (
    <section id="top" className="relative min-h-[92vh] w-full overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20hero%20background%20for%20a%20film%20and%20series%20discovery%20site%2C%20flowing%20warm%20amber%20and%20deep%20vermilion%20light%20gradients%20over%20dark%20textured%20surface%2C%20subtle%20film%20grain%20and%20bokeh%2C%20dramatic%20moody%20atmosphere%2C%20minimalist%20artistic%20composition%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=hero-cupfox-bg&orientation=landscape"
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/85 via-foreground-950/70 to-foreground-950/90"></div>

      <div className="relative z-10 mx-auto flex min-h-[92vh] w-full max-w-6xl flex-col px-4 pb-12 pt-24 md:px-6 md:pt-28">
        <div className="mb-8 overflow-hidden rounded-full border border-background-50/20 bg-background-50/10 backdrop-blur-sm">
          <div className="flex items-center gap-3 border-b border-background-50/10 px-5 py-2">
            <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-500"></span>
              今日更新
            </span>
            <span className="hidden text-xs text-background-100/60 sm:block">滚动查看最新资源动态</span>
          </div>
          <div className="flex w-max animate-cf-ticker py-2.5">
            {[...updates, ...updates].map((u, index) => (
              <span
                key={`${u.id}-${index}`}
                className="flex items-center gap-2 whitespace-nowrap px-5 text-sm"
              >
                <i className="ri-fire-line text-primary-400"></i>
                <span className="font-medium text-background-50">{u.title}</span>
                <span className="text-background-100/85">{u.info}</span>
                <span className="text-xs text-background-100/55">{u.time}</span>
                <span className="text-background-50/20">|</span>
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <span className="animate-cf-fade-up inline-flex items-center gap-2 rounded-full border border-background-50/20 bg-background-50/10 px-4 py-1.5 text-xs text-background-100/90 md:text-sm">
            <i className="ri-magic-line text-primary-300"></i>
            热播剧 · 电影推荐 · 蓝光画质 · 高分片单
          </span>

          <h1 className="animate-cf-fade-up mt-6 font-heading text-4xl font-semibold leading-tight text-background-50 md:text-6xl">
            茶杯狐影视
            <span className="mt-4 block text-lg font-normal leading-relaxed text-background-100/90 md:text-2xl">
              热播剧、电影推荐与全网资源，蓝光画质高分片单一站挖
            </span>
          </h1>

          <p className="animate-cf-fade-up mt-6 max-w-2xl text-sm leading-relaxed text-background-100/80 md:text-base">
            这里把热播剧跟播进度、电影口碑推荐、蓝光画质资源与高分片单集中整理在一起。不用在多个页面之间来回切换，
            先用一句话锁定方向，再按类型和画质收口，就能得到一份更清晰的找片路径。
          </p>

          <div className="animate-cf-fade-up mt-8 w-full max-w-2xl">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(value);
              }}
              className="flex flex-col gap-3 rounded-lg border border-background-50/20 bg-background-50/10 p-2 backdrop-blur-sm sm:flex-row sm:items-center sm:rounded-full"
            >
              <div className="flex flex-1 items-center gap-2 px-4">
                <i className="ri-search-line text-base text-background-100/70"></i>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="输入片名、类型或地区，如「悬疑」「纪录片」"
                  aria-label="搜索站内影视资源"
                  className="h-11 w-full bg-transparent text-sm text-background-50 placeholder:text-background-100/55 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer sm:rounded-full"
              >
                <i className="ri-search-line"></i>
                在资源库中查找
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs text-background-100/60">试试：</span>
              {quickChips.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => handleSubmit(chip)}
                  className="whitespace-nowrap rounded-full border border-background-50/25 bg-background-50/5 px-3 py-1 text-xs text-background-100/90 transition-colors hover:border-primary-400 hover:text-background-50 cursor-pointer"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          <dl className="animate-cf-fade-up mt-10 grid w-full max-w-2xl grid-cols-3 gap-3 md:gap-5">
            {[
              { label: "本页收录条目", value: `${stats.total} 部` },
              { label: "蓝光 4K 片源", value: `${stats.blu} 部` },
              { label: "覆盖内容类型", value: `${stats.types} 类` },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-background-50/15 bg-background-50/5 px-3 py-4 backdrop-blur-sm"
              >
                <dt className="text-xs text-background-100/65">{item.label}</dt>
                <dd className="mt-1 font-heading text-xl font-semibold text-background-50 md:text-2xl">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}